import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CSVDataViewer from '../../components/admin/CSVDataViewer';
import styles from './CSVLiveViewPage.module.css';

const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const CSVLiveViewPage = () => {
  const { slug, uploadId } = useParams();

  // Page state
  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auth state
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Data state
  const [uploadData, setUploadData] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  // Load page info on mount
  useEffect(() => {
    const loadPageInfo = async () => {
      try {
        const response = await fetch(`/api/csv/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Page not found');
          }
          throw new Error('Failed to load page');
        }
        const data = await response.json();
        setPageInfo(data.page);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPageInfo();
  }, [slug]);

  // Load data after authentication
  const loadData = useCallback(async (accessToken) => {
    setDataLoading(true);
    setDataError(null);

    try {
      const response = await fetch(`/api/csv/${slug}/data/${uploadId}?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to load data');
      }

      const data = await response.json();
      setUploadData(data.upload);
      setCsvData(data.data);
    } catch (err) {
      setDataError(err.message);
    } finally {
      setDataLoading(false);
    }
  }, [slug, uploadId]);

  // Handle password verification
  const handleVerify = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setVerifying(true);

    try {
      const response = await fetch(`/api/csv/${slug}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || 'Invalid password');
      }

      const data = await response.json();
      setToken(data.token);
      setAuthenticated(true);
      setPassword(''); // Clear password from state

      // Load data with the token
      await loadData(data.token);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <LoadingSpinner />
            <p>Loading page...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.errorState}>
            <div className={styles.errorIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h1>Page Not Found</h1>
            <p>The data you&apos;re looking for doesn&apos;t exist or is no longer available.</p>
            <Link to="/" className={styles.homeLink}>Go to Homepage</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={authenticated ? styles.wideContainer : styles.container}>
        {/* Logo */}
        <div className={styles.logo}>
          <img loading="lazy" src="/oval-logo.webp" alt="OVAL" />
        </div>

        {/* Page Header */}
        <div className={styles.header}>
          <h1>{pageInfo.name}</h1>
          {pageInfo.agency && (
            <span className={styles.agencyBadge}>{pageInfo.agency.name}</span>
          )}
        </div>

        {/* Password Form */}
        {!authenticated && (
          <div className={styles.card}>
            <h2>Enter Password to View Data</h2>
            <p className={styles.cardDescription}>
              Please enter the password provided to you to access this data.
            </p>

            <form onSubmit={handleVerify} className={styles.passwordForm}>
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={verifying}
                autoFocus
              />
              {authError && (
                <div className={styles.authError}>{authError}</div>
              )}
              <Button type="submit" disabled={!password || verifying} fullWidth>
                {verifying ? 'Verifying...' : 'View Data'}
              </Button>
            </form>
          </div>
        )}

        {/* Data Loading */}
        {authenticated && dataLoading && (
          <div className={styles.card}>
            <div className={styles.loading}>
              <LoadingSpinner />
              <p>Loading data...</p>
            </div>
          </div>
        )}

        {/* Data Error */}
        {authenticated && dataError && (
          <div className={styles.card}>
            <div className={styles.errorState}>
              <div className={styles.errorIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h2>Error Loading Data</h2>
              <p>{dataError}</p>
              <Button onClick={() => loadData(token)} variant="outline">
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* Data Display */}
        {authenticated && csvData && uploadData && (
          <>
            {/* Upload Info Card */}
            <div className={styles.infoCard}>
              <div className={styles.infoRow}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>File</span>
                  <span className={styles.infoValue}>{uploadData.fileName}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Uploaded</span>
                  <span className={styles.infoValue}>{formatDate(uploadData.uploadedAt)}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Rows</span>
                  <span className={styles.infoValue}>{uploadData.rowCount?.toLocaleString()}</span>
                </div>
              </div>
              <Link to={`/csv/${slug}`} className={styles.backLink}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                Back to Upload
              </Link>
            </div>

            {/* Data Viewer */}
            <div className={styles.viewerCard}>
              <CSVDataViewer
                headers={csvData.headers}
                data={csvData.rows}
                filename={uploadData.fileName}
                pageSize={25}
                showExport={true}
                showSearch={true}
                showColumnToggle={true}
                showPagination={true}
              />
            </div>
          </>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <p>Powered by <a href="/">OVAL</a></p>
        </div>
      </div>
    </div>
  );
};

export default CSVLiveViewPage;
